/**
 * PageHeader Component
 *
 * Consistent page header used across the app:
 *  - title: page title (h4 on sm+, h5 on xs to save vertical space)
 *  - subtitle: secondary line under the title (optional)
 *  - icon: optional leading icon, colored with primary
 *  - actions: optional right-aligned buttons/controls
 *
 * Sticky on desktop (sm+) so the title stays visible while the page
 * content scrolls. Not sticky on mobile — keeps the AppBar exclusive
 * and avoids double-sticky headers on small viewports.
 */
import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  /**
   * Optional element rendered inline next to the title (e.g. info button,
   * status chip). Stays aligned with the title and doesn't push the title
   * off the baseline; the subtitle stays underneath as before.
   */
  titleTrailing?: ReactNode;
  /** When true the header sticks to the top of its scroll container (sm+ only). */
  sticky?: boolean;
}

export function PageHeader({
  title,
  subtitle,
  icon,
  actions,
  titleTrailing,
  sticky = false,
}: PageHeaderProps) {
  return (
    <Box
      sx={{
        position: sticky ? { sm: 'sticky' } : 'static',
        top: sticky ? { sm: 0 } : undefined,
        zIndex: sticky ? { sm: 2 } : undefined,
        bgcolor: 'background.default',
        pt: { xs: 1, sm: 0 },
        pb: 2,
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 2 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        gap={2}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          {icon && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(139, 92, 246, 0.15)'
                    : 'rgba(79, 70, 229, 0.10)',
                color: 'primary.main',
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
              <Typography
                variant="h5"
                component="h1"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.15rem', sm: '1.5rem' },
                  lineHeight: 1.25,
                }}
              >
                {title}
              </Typography>
              {titleTrailing}
            </Stack>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.4 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {actions && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              flexShrink: 0,
              width: { xs: '100%', sm: 'auto' },
              justifyContent: { xs: 'flex-end', sm: 'flex-end' },
            }}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default PageHeader;
